/**
 * "Start a support session" — the superadmin entry point into a tenant
 * (SUPPORT-IMPERSONATION). Since superadmins have no standing access to a
 * tenant's data, this is how they get in: pick one of the tenant's admins
 * to assume, choose read-only vs read-write, and give a reason (logged).
 * On start it swaps identity and lands on the dashboard as that admin.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { listTenantAdmins, type TenantAdmin } from '../api/tenants';
import { extractErrorMessage } from '../api/errors';
import { useImpersonation } from '../auth/ImpersonationContext';
import { SearchableSelect } from './SearchableSelect';

interface Props {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  tenantName: string;
}

export function StartSupportSessionDialog({
  open,
  onClose,
  tenantId,
  tenantName,
}: Props) {
  const { enter } = useImpersonation();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState<TenantAdmin[]>([]);
  const [targetId, setTargetId] = useState('');
  const [readOnly, setReadOnly] = useState(true);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setTargetId('');
    setReason('');
    setReadOnly(true);
    void listTenantAdmins(tenantId)
      .then((a) => setAdmins(a.filter((x) => x.is_active)))
      .catch(() => setError('Could not load this tenant’s admins.'));
  }, [open, tenantId]);

  const adminOptions = useMemo(
    () =>
      admins.map((a) => ({
        value: a.id,
        label: `${a.full_name || a.email} · ${a.role}`,
      })),
    [admins],
  );
  const target = admins.find((a) => a.id === targetId) ?? null;
  const canStart = Boolean(target) && reason.trim().length > 0 && !busy;

  const handleStart = async () => {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      await enter({
        targetUserId: target.id,
        tenantId,
        readOnly,
        reason: reason.trim(),
        level: {
          role: 'tenant_admin',
          label: target.full_name || target.email,
          tenantName,
          readOnly,
        },
      });
      onClose();
      navigate('/dashboard');
    } catch (e) {
      setError(extractErrorMessage(e, 'Could not start the session.'));
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Start a support session</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            You’ll act as this admin in <strong>{tenantName}</strong>. Every
            action is logged under your account, and you can exit any time from
            the banner.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <SearchableSelect
            label="Assume tenant admin"
            size="medium"
            value={targetId}
            onChange={setTargetId}
            options={adminOptions}
            placeholder={
              admins.length === 0 ? 'No active admins to assume' : undefined
            }
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={readOnly}
                onChange={(e) => setReadOnly(e.target.checked)}
              />
            }
            label={readOnly ? 'Read-only (recommended)' : 'Read-write'}
          />
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            multiline
            minRows={2}
            fullWidth
            helperText="Required — stored on the audit trail."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleStart()}
          disabled={!canStart}
        >
          {busy ? 'Starting…' : 'Start session'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
